/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { BEM } from "@adapptio/lib-components";
import { FC_ANCHOR, FC_DIRECTION, FC_POSITION, FC_SIZE, FloatingContainer } from "../FloatingContainer";
import { MenuList } from "../MenuList";
import { FadeInOut } from "../FadeInOut";

import "./Menu.scss";
import { ClickAwayListener } from "../ClickAwayListener";

export interface IMenuProps {
	id?: string;
	children?: React.ReactNode;
	className?: string;
	open?: boolean;
	anchorRef: React.MutableRefObject<HTMLElement>;
	containerRef?: React.MutableRefObject<HTMLDivElement>;
	onClose?: () => void;
	autoFocusItem?: boolean;
}

const Menu: React.FC<IMenuProps> = ({
	children,
	className,
	open,
	anchorRef,
	containerRef,
	onClose,
	autoFocusItem
}) => {
	return (
		<FadeInOut
			isVisible={open}
			fadeOutDelay={250}
			render={(isVisible) => {
				const baseClass = BEM("edt-menu", null, {
					open,
					visible: isVisible
				});

				if (className) {
					baseClass.add(className);
				}

				return (
					<ClickAwayListener onClickAway={onClose}>
						<FloatingContainer
							parent={anchorRef}
							ref={containerRef}
							anchor={FC_ANCHOR.CENTER_BOTTOM}
							position={FC_POSITION.AUTO}
							direction={FC_DIRECTION.HR}
							size={FC_SIZE.FIT_MIN}
							className={baseClass.toClassName()}
							onClose={onClose}
						>
							<MenuList
								role="listbox"
								autoFocusItem={autoFocusItem}
								className={baseClass.element("options").toClassName()}
								disableListWrap
								variant="selectedMenu"
							>
								{children}
							</MenuList>
						</FloatingContainer>
					</ClickAwayListener>
				);
			}}
		/>
	);
};

const MenuMemo = React.memo(Menu);

export { MenuMemo as Menu };
